import { useMemo, useState, useEffect, useCallback } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import * as routerActions from 'connected-react-router'
import { useLocation } from 'react-router-dom'
import { stringify, parse } from 'querystringify'

// Actions
import { actions as audioActions } from 'modules/audio/actions'
import { actions as authActions } from 'modules/auth/actions'
import { actions as collectionsActions } from 'modules/collections/actions'
import { actions as downloadActions } from 'modules/download/actions'

// Selectors
import { selectEntity } from 'modules/discover/selectors'

// Hooks
import { useRequest } from 'modules/requests/hooks'
import { useProfile } from 'modules/profile/hooks'
import { useCollections, useTracks } from 'modules/entities/hooks'
import { useSubscription } from 'modules/subscription/hooks'
import { useDiscounts } from 'modules/price/hooks'

// Types
import { ApiRequest, State, AudioState, Loop } from 'types'

// Utils
import { isJson } from 'utils'
import { PREVIEW_LIMIT } from '@constants'
import { audioBackend } from 'modules/audio/backend'
import { daysSince } from 'lib/formatdate'

export const useAudio = (): {
  loop: Loop | null | undefined
  songItemId: string
  trackId: number
  contextKey: string
  isPlaying: boolean
} => {
  const {
    songItemId,
    trackId,
    currentPlaylist: contextKey,
    currentLoop: loop,
    isPlaying,
  } = useSelector<State, AudioState>(state => state.audio)
  return useMemo(() => {
    return { loop, songItemId, trackId, contextKey, isPlaying }
  }, [loop, songItemId, trackId, contextKey, isPlaying])
}

// returns playing states for the provided songItemId and context (compared to useAudio above which returns the currently playing)
export const useAudioPlayStates = (
  songItemId: string,
  contextKey: string,
): {
  isPlaying: boolean
  isLoading: boolean
  isLoadingLoop: boolean
  isLooping: boolean
  isActive: boolean
} => {
  const audio = useSelector<State, AudioState>(state => state.audio)
  const isActive = songItemId === audio.songItemId && audio.currentPlaylist === contextKey
  const isPlaying = isActive && audio.isPlaying && !audio.isPaused
  const isLoading = isActive && audio.isLoading
  const isLoadingLoop = useRequest('FETCH_TRACK_FEATURE_DATA').isLoading && isActive
  const isLooping = isActive && audio.isLooping

  return useMemo(() => {
    return { isPlaying, isLoading, isLoadingLoop, isLooping, isActive }
  }, [isPlaying, isLoading, isLoadingLoop, isLooping, isActive])
}

export const useAudioContext = () => {
  const state = useSelector<State, State>(s => s)

  const { currentPlaylist } = state.audio
  if (!isJson(currentPlaylist)) {
    return null
  }

  const parsedContext = JSON.parse(currentPlaylist)
  if (!Object.keys(parsedContext)?.length) {
    return null
  }

  let type = Object.keys(parsedContext)[0]
  let value: number | null | undefined | string = parsedContext[type]

  if (type === 'owned') {
    return { title: 'Purchased tracks', url: '/purchasedtracks/' }
  }

  // Song search param is 'id'
  if (type === 'id') {
    type = 'song'
  }

  const { artists, labels } = state.entities
  // Convert artist slug to id
  if (type === 'artist') {
    const artistList = Object.keys(artists).map(id => artists[id])
    value = artistList.find(x => x.slug === value)?.id
  }

  // Convert lael slug to id
  if (type === 'label') {
    const labelList = Object.keys(labels).map(id => labels[id])
    value = labelList.find(x => x.slug === value)?.id
  }

  const entity = value && selectEntity(state, type.endsWith('s') ? type.slice(0, -1) : type, value)
  if (entity) {
    return { title: entity.title, url: entity.url }
  }
  return null
}

export const useCurrentPos = (interval = 150): number => {
  const [currentPos, setCurrentPos] = useState(0)
  const { songItemId, trackId, currentPlaylist: contextKey } = useSelector<State, AudioState>(state => state.audio)
  const { isActive, isPlaying } = useAudioPlayStates(songItemId, contextKey)

  useEffect(() => {
    setCurrentPos(0)
  }, [trackId])

  useEffect(() => {
    let posInterval: NodeJS.Timeout
    if (isActive && isPlaying) {
      posInterval = setInterval(() => {
        setCurrentPos(audioBackend?.getCurrentTime() || 0)
      }, interval)
    }
    return () => clearInterval(posInterval)
  })

  return currentPos
}

export const useLoop = (): {
  loop: Loop | null | undefined
  bars: number
  beatsPerBar: number
  request: ApiRequest
} => {
  const { trackId, currentLoop: loop, features } = useSelector<State, AudioState>(state => state.audio)
  const beatsPerBar = features[String(trackId)]?.beats_per_bar
  const bars = (loop && loop.beat_count / beatsPerBar) || 2
  const request = useRequest('FETCH_TRACK_FEATURE_DATA')

  return useMemo(() => {
    return { loop, bars, beatsPerBar, request }
  }, [loop, bars, beatsPerBar, request])
}

export const useActions = () => {
  const dispatch = useDispatch()
  const profile = useProfile()
  const { search } = useLocation()
  const collections = useCollections()
  const { loop: currentLoop, trackId: currentTrackId } = useAudio()
  const tracks = useTracks()
  const useTrackPreview = profile?.id === 0 || !profile
  const subscription = useSubscription()
  const discounts = useDiscounts()
  const discountList = Object.keys(discounts).map(id => discounts[id])

  const shouldDisableNativeButtonClick = (e: React.MouseEvent) => {
    // Disable native spacebar "click" (keyCode 32)
    return e?.nativeEvent && e?.nativeEvent?.screenX === 0 && e?.nativeEvent?.screenY === 0 && e?.keyCode === 32
  }

  const handleSkip = useCallback(
    (event: KeyboardEvent | undefined, prev: boolean) => {
      if (event) {
        event.preventDefault()
      }
      return shouldDisableNativeButtonClick(event) ? false : dispatch(audioActions.skip(!prev))
    },
    [dispatch],
  )

  const handleTogglePause = useCallback(
    (event?: Event) => {
      if (event) {
        event.preventDefault()
      }

      return shouldDisableNativeButtonClick(event) ? false : dispatch(audioActions.togglePaused())
    },
    [dispatch],
  )

  const handleSeek = useCallback(
    (units: number) => {
      // Units = bars for loop, seconds for non-loops
      dispatch(audioActions.setIsHotkeySeeking(true))
      const pos = audioBackend?.getCurrentTime() || 0

      if (useTrackPreview && pos >= PREVIEW_LIMIT) {
        return dispatch(authActions.showSignupModal('play'))
      }
      if (currentLoop) {
        return dispatch(audioActions.moveLoop(units))
      }
      if (pos + units > (audioBackend?.audioElement.duration || 0)) {
        return handleSkip(undefined, true)
      } else if (+pos + units < 0) {
        return handleSkip(undefined, false)
      } else {
        return dispatch(audioActions.seekToTime(+pos, units))
      }
    },
    [dispatch, handleSkip, currentLoop, useTrackPreview],
  )

  const handleOffset = useCallback(
    (offset: number) => {
      dispatch(audioActions.setIsHotkeySeeking(true))
      return dispatch(audioActions.offsetBeat(offset))
    },
    [dispatch],
  )

  const handleLoopToggle = useCallback(() => {
    return dispatch(audioActions.toggleLoop())
  }, [dispatch])

  const handleAddToCollection = useCallback(
    (trackId?: number, loop?: Loop) => {
      const trackIdToSave = trackId || currentTrackId
      const loopToSave = loop || currentLoop
      const newSearch = `modal=addtrack&trackId=${trackIdToSave}${
        loopToSave ? `&loop=${loopToSave.beat_start}:${loopToSave.beat_count}` : ''
      }`
      return dispatch(routerActions.push({ search: [search.replace('?', ''), newSearch].join('&') }))
    },
    [dispatch, currentLoop, currentTrackId, search],
  )

  const collectionId = profile?.favorites_collection_id
  const handleAddToFavorites = useCallback(
    (trackId?: number, loop?: Loop | null) => {
      if (!collectionId) {
        return dispatch(authActions.showSignupModal('favorite'))
      }
      const trackIdToSave = trackId || currentTrackId
      const loopToSave = loop || currentLoop

      const collection = collections[String(collectionId)]
      const isInFavorites = loopToSave
        ? collection?.tracks.some(
            ({ id, loop: innerLoop }) =>
              innerLoop &&
              id === trackIdToSave &&
              innerLoop.beat_count === loopToSave.beat_count &&
              innerLoop.beat_start === loopToSave.beat_start,
          )
        : collection?.tracks.some(({ id, loop: innerLoop }) => +id === +trackIdToSave && innerLoop === null)

      return isInFavorites
        ? dispatch(collectionsActions.removeTrackFromCollection(+trackIdToSave, collectionId, loopToSave, String(trackIdToSave)))
        : dispatch(collectionsActions.addTrackToCollection(+trackIdToSave, collectionId, loopToSave, String(trackIdToSave)))
    },
    [dispatch, collections, currentLoop, currentTrackId, collectionId],
  )

  const isVerified = profile?.emails?.some(x => x.verified)
  const profileId = profile?.id
  const handleDownload = useCallback(
    (trackId?: number, loop?: Loop | null | undefined) => {
      if (profileId === 0) {
        return dispatch(authActions.showSignupModal('download'))
      }
      const currentQuery = parse(search)
      if (!isVerified) {
        const newSearch = stringify({
          ...currentQuery,
          modal: 'verification',
        })
        return dispatch(routerActions.push(`?${newSearch}`))
      }

      const trackIdToDownload = trackId || currentTrackId
      const loopToDownload = loop || (trackId === currentTrackId ? currentLoop : null)
      const isOwned = !!profile.purchased_tracks.find(x => +x.track === +trackIdToDownload)

      if (!isOwned) {
        const canSpendCredits = ['ACTIVE', 'TRIAL', 'CANCELLED'].includes(subscription?.state)
        if (!canSpendCredits) {
          const newSearch = stringify({
            ...currentQuery,
            modal: 'credits',
          })
          return dispatch(routerActions.push(`?${newSearch}`))
        }

        const isFreeTack = discountList.find(
          discount => (discount.type === 'track' && discount.object_id === +(trackId || -99)) || discount.type === 'alltracks',
        )
        if (subscription.credit_count === 0 && !isFreeTack) {
          const newSearch = stringify({
            ...currentQuery,
            modal: 'download',
            trackId: trackIdToDownload,
          })
          return dispatch(routerActions.push(`?${newSearch}`))
        }
      }

      const filename = tracks[String(trackIdToDownload)].song_title

      const newSearch = stringify({
        ...currentQuery,
        modal: 'download',
        trackId: trackIdToDownload,
        ...(loop ? { loop: `${loop.beat_start}:${loop.beat_count}` } : {}),
      })

      const lastPurchaseCostReminder = localStorage.getItem('lastPurchaseCostReminder')
      const shouldOpenDownloadModal = !isOwned && (!lastPurchaseCostReminder || daysSince(lastPurchaseCostReminder) >= 365)
      return shouldOpenDownloadModal
        ? dispatch(routerActions.push(`?${newSearch}`))
        : dispatch(downloadActions.waitForDownloadLinkThenDownload(trackIdToDownload, loopToDownload, filename))
    },
    [dispatch, currentLoop, currentTrackId, profile, tracks, isVerified, subscription, search, profileId, discountList],
  )

  return useMemo(() => {
    return {
      handleSkip,
      handleSeek,
      handleOffset,
      handleLoopToggle,
      handleAddToCollection,
      handleTogglePause,
      handleAddToFavorites,
      handleDownload,
    }
  }, [
    handleSkip,
    handleSeek,
    handleOffset,
    handleLoopToggle,
    handleAddToCollection,
    handleTogglePause,
    handleAddToFavorites,
    handleDownload,
  ])
}
